import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, Plus } from 'lucide-react';
import { todayInTz, type CurrencyCode, type TransactionDto } from '@trestly/shared';
import { useBudgets, useCategories, useTransactions } from '../api/hooks';
import { usePeriod } from '../period-context';
import { useQuickExpense } from '../quick-expense';
import { ScreenHeader } from '../components/ScreenHeader';
import { EnvelopeSheet } from '../components/EnvelopeSheet';
import { TransactionSheet } from '../components/TransactionSheet';
import { useSheetState } from '../components/useSheetState';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { ListSkeleton } from '../components/Skeleton';
import { envelopeColor, NO_CATEGORY } from '../lib/envelope';
import { errorText } from '../lib/errors';
import { dayHeading } from '../lib/format';
import { currencySign, money, moneyLabel } from '../lib/money';
import { periodLabel } from '../lib/period';
import { haptic, useBackButton } from '@/telegram';
import { cn } from '@/lib/utils';
import { t } from '../i18n';

/**
 * Конверт целиком: сколько осталось, траты периода и правка лимита, имени и
 * цвета. /envelopes/none — траты без категории: у них нет лимита и правки.
 */
export function Envelope(): JSX.Element {
  const params = useParams();
  const navigate = useNavigate();
  const categoryId = params.id === 'none' ? null : Number(params.id);
  const { period } = usePeriod();
  const budgets = useBudgets(period);
  const categories = useCategories();
  // Фильтр по категории — на сервере; «без категории» сервер не фильтрует, берём все и отбираем.
  const transactions = useTransactions(period, categoryId ?? undefined);
  const quick = useQuickExpense();
  const [editing, setEditing] = useState(false);
  const editingTx = useSheetState<TransactionDto>();

  const back = useCallback(() => navigate('/'), [navigate]);
  useBackButton(back);

  const budget = categoryId === null ? null : budgets.data?.items.find((item) => item.categoryId === categoryId);
  const currency: CurrencyCode = budgets.data?.baseCurrency ?? 'BYN';
  const color = budget ? envelopeColor(budget.categoryColor, budget.categoryPosition) : NO_CATEGORY.color;
  const name = categoryId === null ? NO_CATEGORY.name : (budget?.categoryName ?? t('envelopePage.fallbackName'));

  const days = useMemo(() => {
    const groups = new Map<string, TransactionDto[]>();
    for (const tx of transactions.data ?? []) {
      if (categoryId === null && tx.categoryId !== null) continue;
      const list = groups.get(tx.spentOn) ?? [];
      list.push(tx);
      groups.set(tx.spentOn, list);
    }
    return [...groups.entries()].sort(([a], [b]) => (a < b ? 1 : -1));
  }, [transactions.data, categoryId]);

  const count = days.reduce((sum, [, list]) => sum + list.length, 0);
  const todayIso = todayInTz();

  // Конверт убрали в архив или его нет в периоде — показывать нечего.
  const missing = categoryId !== null && budgets.isSuccess && !budget;

  return (
    <div style={{ '--c': color } as React.CSSProperties}>
      <ScreenHeader
        title={name}
        onBack={back}
        action={
          budget ? (
            <button
              type="button"
              aria-label={t('envelopePage.edit')}
              className="icon-btn"
              onClick={() => {
                haptic.tap();
                setEditing(true);
              }}
            >
              <Pencil className="size-[18px]" aria-hidden />
            </button>
          ) : null
        }
      />

      <div className="px-4">
        {budgets.isError ? (
          <ErrorState message={errorText(budgets.error)} onRetry={() => void budgets.refetch()} />
        ) : missing ? (
          <div className="panel">
            <EmptyState title={t('envelopePage.missingTitle')} description={t('envelopePage.missingDesc', { period: periodLabel(period) })} />
          </div>
        ) : (
          <>
            <Hero
              limitMinor={budget?.limitMinor ?? 0}
              spentMinor={
                budget?.spentMinor ??
                (categoryId === null ? (budgets.data?.uncategorizedSpentMinor ?? 0) : 0)
              }
              carriedMinor={budget?.carriedMinor ?? 0}
              currency={currency}
              loading={budgets.isPending}
            />

            <button
              type="button"
              className="mb-6 flex h-[52px] w-full items-center justify-center gap-2 rounded-full text-[16px] font-semibold text-white active:opacity-85"
              style={{ background: color }}
              onClick={() => quick.open({ categoryId })}
            >
              <Plus className="size-5" aria-hidden />
              {t('envelopePage.addTo', { name })}
            </button>

            <div className="flex items-baseline justify-between px-1 pb-1.5">
              <h2 className="section-title">
                {t('envelopePage.txs')}{count > 0 ? ` · ${count}` : ''}
              </h2>
            </div>

            {transactions.isPending ? (
              <ListSkeleton rows={3} />
            ) : transactions.isError ? (
              <ErrorState message={errorText(transactions.error)} onRetry={() => void transactions.refetch()} />
            ) : days.length === 0 ? (
              <div className="panel">
                <EmptyState title={t('envelopePage.emptyTitle')} description={t('envelopePage.emptyDesc', { period: periodLabel(period) })} />
              </div>
            ) : (
              <div className="space-y-4">
                {days.map(([day, list]) => (
                  <section key={day}>
                    <div className="flex items-baseline justify-between px-1 pb-1.5">
                      <h3 className="section-title">{dayHeading(day, todayIso)}</h3>
                      <span className="num text-[12.5px] text-faint">
                        {money(
                          list.reduce((sum, tx) => sum + tx.amountBaseMinor, 0),
                          list[0]?.baseCurrency ?? currency,
                        )}
                      </span>
                    </div>
                    <ul className="panel overflow-hidden">
                      {list.map((tx) => (
                        <li key={tx.id}>
                          <button
                            type="button"
                            className="row-press flex min-h-[52px] w-full items-center gap-3 px-4 py-2 text-left"
                            onClick={() => {
                              haptic.tap();
                              editingTx.show(tx);
                            }}
                          >
                            <span className={cn('min-w-0 flex-1 truncate text-[15px]', !tx.note && 'text-muted-foreground')}>
                              {tx.note ?? t('envelopePage.noNote')}
                            </span>
                            <span className="text-right">
                              <span className="num block text-[15px] font-semibold">
                                {moneyLabel(tx.amountMinor, tx.currency)}
                              </span>
                              {tx.currency !== tx.baseCurrency ? (
                                <span className="num block text-[11.5px] text-faint">
                                  ≈ {money(tx.amountBaseMinor, tx.baseCurrency)}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {budget ? (
        <EnvelopeSheet
          open={editing}
          budget={budget}
          period={period}
          baseCurrency={currency}
          onClose={() => setEditing(false)}
          onArchived={back}
        />
      ) : null}

      {editingTx.value ? (
        <TransactionSheet
          key={editingTx.value.id}
          open={editingTx.open}
          transaction={editingTx.value}
          categories={categories.data ?? []}
          onClose={editingTx.close}
        />
      ) : null}
    </div>
  );
}

/** Большая плитка конверта: остаток крупно, «жидкость» — доля лимита, что осталась. */
function Hero({
  limitMinor,
  spentMinor,
  carriedMinor,
  currency,
  loading,
}: {
  limitMinor: number;
  spentMinor: number;
  carriedMinor: number;
  currency: CurrencyCode;
  loading: boolean;
}): JSX.Element {
  if (loading) return <div className="mb-4 h-[168px] animate-pulse rounded-[26px] bg-muted" />;

  const hasLimit = limitMinor > 0;
  const remaining = limitMinor - spentMinor;
  const over = hasLimit && remaining < 0;
  const share = hasLimit ? Math.max(0, Math.min(1, remaining / limitMinor)) : 0;

  return (
    <div
      className={cn('panel relative mb-4 overflow-hidden px-5 pt-5 pb-5', over && 'envelope-over')}
    >
      <span className="absolute inset-x-0 top-0 h-1" style={{ background: 'var(--c)' }} aria-hidden />
      <div className="relative">
        <div className="text-[13px] font-medium text-muted-foreground">
          {!hasLimit ? t('envelopePage.spent') : over ? t('envelopePage.over') : t('envelopePage.left')}
        </div>
        <div className={cn('num-hero mt-1 text-[46px] leading-none font-semibold', over && 'text-destructive')}>
          {money(hasLimit ? remaining : spentMinor, currency)}
          <span className="ml-1.5 text-[26px] font-semibold text-muted-foreground">{currencySign(currency)}</span>
        </div>
        <div className="num mt-2 text-[13px] text-muted-foreground">
          {hasLimit
            ? t('envelopePage.spentOf', { spent: money(spentMinor, currency), limit: money(limitMinor, currency), pct: Math.round((spentMinor / limitMinor) * 100) })
            : t('envelopePage.noLimit')}
          {carriedMinor > 0 ? ` · ${t('envelopePage.carried', { sum: money(carriedMinor, currency) })}` : ''}
        </div>
        {hasLimit ? (
          <span className="meter mt-4" aria-hidden>
            <span style={{ width: over ? '100%' : `${Math.round(share * 100)}%` }} />
          </span>
        ) : null}
      </div>
    </div>
  );
}